import { motion } from 'framer-motion';
import { useAuthModal } from '../context/AuthModalContext';
import { scrollToId } from '../utils/scroll';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: 'easeOut' },
  }),
};

const PlansAndPricing = () => {
  const { openModal } = useAuthModal();

  const plans = [
    {
      name: "Monthly",
      price: "$20",
      period: "/month",
      description: "Perfect for trying out daily African language learning with your family.",
      features: [
        "African Word of the Day via WhatsApp",
        "Weekly proverbs & cultural stories",
        "Access on one device",
        "Cancel anytime",
        "Email support",
      ],
      cta: "Subscribe Monthly",
      isHighlighted: false,
      badge: null,
    },
    {
      name: "Yearly",
      price: "$220",
      period: "/year",
      description: "Save $20 a year and give your family a full year of daily learning.",
      features: [
        "Everything in Monthly",
        "Equivalent to 2 months free",
        "Priority WhatsApp support",
        "Early access to new content series",
        "Access on one device",
      ],
      cta: "Subscribe Yearly",
      isHighlighted: true,
      badge: "Best Value",
    },
    {
      name: "Schools & Organizations",
      price: "Custom",
      period: "",
      description: "Tailored plans for schools, communities, and organizations teaching multiple children.",
      features: [
        "Bulk student & family licensing",
        "Custom content requests",
        "Dedicated account manager",
        "Multi-device access for classrooms",
        "Onboarding & training support",
      ],
      cta: "Contact Sales",
      isHighlighted: false,
      badge: "Custom Solution",
      isCustom: true,
    },
  ];

  return (
    <section id="plans" className="py-20 bg-gradient-to-b from-gray-50 to-white scroll-mt-20">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <span className="inline-block bg-indigo-100 text-indigo-800 text-sm font-semibold px-4 py-1 rounded-full mb-4">
            Pricing Plans
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Simple, <span className="text-indigo-600">Transparent</span> Pricing
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            One daily African language lesson, delivered straight to WhatsApp. Choose monthly or save with yearly.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              custom={index}
              variants={fadeUp}
              className={`relative flex flex-col h-full p-8 rounded-2xl transition-all duration-300 ${
                plan.isHighlighted
                  ? "bg-white border-2 border-indigo-500 shadow-xl transform lg:-translate-y-4"
                  : "bg-white border border-gray-200 shadow-lg hover:shadow-xl"
              }`}
            >
              {/* Plan badge */}
              {plan.badge && (
                <div className={`absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 px-4 py-2 rounded-full text-sm font-bold ${
                  plan.isHighlighted 
                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
                    : "bg-gray-100 text-gray-800"
                }`}>
                  {plan.badge}
                </div>
              )}

              {/* Plan header */}
              <div className="mb-6">
                <h3 className={`text-2xl font-bold mb-2 ${
                  plan.isHighlighted ? "text-indigo-600" : "text-gray-900"
                }`}>
                  {plan.name}
                </h3>
                <div className="flex items-end mb-2">
                  <span className="text-4xl font-extrabold text-gray-900">
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-lg text-gray-600 ml-2">{plan.period}</span>
                  )}
                </div>
                <p className="text-gray-600">{plan.description}</p>
              </div>

              {/* Features list */}
              <div className="flex-grow mb-8">
                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <svg
                        className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                          plan.isHighlighted ? "text-indigo-500" : "text-gray-400"
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA button */}
              <div className="mt-auto">
                <button
                  type="button"
                  onClick={() => (plan.isCustom ? scrollToId("contact") : openModal("signup"))}
                  className={`block w-full text-center py-3 px-6 rounded-xl font-semibold transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 ${
                    plan.isHighlighted
                      ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg hover:-translate-y-1"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Schools & organizations note */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-6">
            Teaching a classroom or community group? Ask about our Schools & Organizations plan.
          </p>
          <button
            type="button"
            onClick={() => scrollToId("contact")}
            className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
          >
            Request a Custom Quote
            <svg className="ml-2 -mr-1 w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default PlansAndPricing;